import {
  PostListQuery,
  PostListQueryVariables,
  PostOrderField,
} from '__generated__/graphql/legacy/graphql';
import classNames from 'classnames/bind';
import { features } from 'constants/featureFlags';
import { getTotalNumberOfReplies } from 'helpers/postHelpers';
import { useTranslation } from 'react-i18next';
import { NUMBER_OF_REACTIONS, TRENDING_POSTS_MAX } from 'utils/constants';

import React from 'react';

import { useQuery } from '@apollo/client';

import { P } from '@thinkific/toga-react';

import { useCommunityContext } from 'components/CommunityContext';
import VisibilityCloak from 'components/VisibilityCloak/VisibilityCloak';

import { POST_LIST_QUERY } from 'schema/Post/queries';

import ExplanationCard from './ExplanationCard/ExplanationCard';
import TrendingPost from './TrendingPost/TrendingPost';
import styles from './TrendingPosts.module.scss';
import TrendingPostsSkeleton from './TrendingPostsSkeleton/TrendingPostsSkeleton';

const cx = classNames.bind(styles);

const TrendingPosts = () => {
  const { t } = useTranslation();
  const { community, featureFlags } = useCommunityContext();
  const multipleReactionsEnabled = featureFlags[features.COMMUNITY_REACTIONS] || false;

  const { loading, data } = useQuery<PostListQuery, PostListQueryVariables>(POST_LIST_QUERY, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      communityId: community.id,
      numOfPosts: TRENDING_POSTS_MAX,
      postOrderField: PostOrderField.TrendingScore,
      filter: {
        trending: true,
      },
      numberOfReactions: NUMBER_OF_REACTIONS,
      multipleReactionsEnabled,
    } as PostListQueryVariables & { multipleReactionsEnabled: boolean },
  });

  const posts = data?.community?.posts?.edges || [];

  if (loading) {
    return (
      <VisibilityCloak admins>
        <TrendingPostsSkeleton />
      </VisibilityCloak>
    );
  }

  if (posts.length === 0) {
    return (
      <VisibilityCloak admins>
        <ExplanationCard />
      </VisibilityCloak>
    );
  }

  return (
    <div className={cx('trending-posts')} data-qa="trending-posts">
      <P className={cx('trending-posts__title')}>
        {t('components-trendingPosts_title', 'Trending posts')}
      </P>
      <ul className={cx('trending-posts__list')}>
        {posts.map((edge) => {
          const post = edge?.node;

          if (!post) return null;

          const totalCommentCount = getTotalNumberOfReplies(post);

          return (
            <TrendingPost
              author={post?.user}
              body={post.body || { jsonContent: '', plainText: post.title || '' }}
              commentCount={totalCommentCount}
              id={post.id}
              key={post.id}
            />
          );
        })}
      </ul>
    </div>
  );
};

export default TrendingPosts;
